import { useState, useCallback, useEffect, useRef } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { B, font, space, radius, fonts, weight } from '../constants/brand';
import { fmt } from '../utils/format';
import { api } from '../api/client';
import { Btn, Spinner, ErrorMsg, Card, PageHeader, Icon, Badge, statusColor, EmptyState } from '../components/ui';

// Converts BlockNote JSON or legacy HTML note content to plain text
const noteToText = (content) => {
  if (!content) return "";
  const trimmed = content.trim();
  if (!trimmed.startsWith('[')) {
    // Legacy HTML — strip tags
    return trimmed.replace(/<[^>]+>/g, ' ').replace(/\s{2,}/g, ' ').trim();
  }
  try {
    const blocks = JSON.parse(trimmed);
    const extract = (blocks) =>
      blocks.flatMap(block => {
        const inline = (block.content || [])
          .filter(c => c.type === 'text')
          .map(c => c.text)
          .join('');
        const children = extract(block.children || []);
        return [inline, ...children].filter(Boolean);
      });
    return extract(blocks).join(' ').replace(/\s{2,}/g, ' ').trim();
  } catch {
    return content;
  }
};

// Activity type → brand color (routed through the shared Badge, not ad-hoc pastels).
const TYPE_BADGE = { Note: B.navy, Task: B.green };
const TypeBadge = ({ type }) => <Badge color={TYPE_BADGE[type] || B.grey}>{type}</Badge>;

// Sub-type → brand token. Rendered as a small tinted uppercase chip.
const SUBTYPE_TOKEN = {
  Created: B.navy, Updated: B.amber, Completed: B.green, "Due Date Changed": B.gold,
};
const SUBTYPE_ORDER = { Created: 0, Updated: 1, Completed: 2 };

// Within a deal group, merge all events for the same task (Created / Updated /
// Completed plus any notes linked to it via taskID) into a single combined row.
// Notes without a linked task in the date range remain as their own rows.
const combineTaskEvents = (items) => {
  const taskGroups = new Map();

  for (const row of items) {
    if (row.activityType !== 'Task' || !row.taskID) continue;
    let g = taskGroups.get(row.taskID);
    if (!g) {
      g = {
        _kind: 'task',
        activityType: 'Task',
        deal: row.deal,
        dealID: row.dealID,
        dealDetailID: row.dealDetailID,
        taskID: row.taskID,
        taskTitle: row.description,
        activityDate: row.activityDate,
        subTypes: [],
        notes: [],
        authors: [],
        assignedTo: row.assignedTo,
        priority: row.priority,
        status: row.status,
        dueDate: row.dueDate,
      };
      taskGroups.set(row.taskID, g);
    }
    if (row.activitySubType && !g.subTypes.includes(row.activitySubType)) {
      g.subTypes.push(row.activitySubType);
    }
    if (new Date(row.activityDate) > new Date(g.activityDate)) g.activityDate = row.activityDate;
    if (row.author && !g.authors.includes(row.author)) g.authors.push(row.author);
    if (row.assignedTo) g.assignedTo = row.assignedTo;
    if (row.priority)   g.priority   = row.priority;
    if (row.status)     g.status     = row.status;
    if (row.dueDate)    g.dueDate    = row.dueDate;
  }
  for (const g of taskGroups.values()) {
    g.subTypes.sort((a, b) => (SUBTYPE_ORDER[a] ?? 99) - (SUBTYPE_ORDER[b] ?? 99));
  }

  const consumedNoteIds = new Set();
  for (const row of items) {
    if (row.activityType === 'Note' && row.taskID && taskGroups.has(row.taskID)) {
      const g = taskGroups.get(row.taskID);
      g.notes.push({
        noteID: row.noteID,
        description: row.description,
        author: row.author,
        activityDate: row.activityDate,
      });
      if (row.author && !g.authors.includes(row.author)) g.authors.push(row.author);
      consumedNoteIds.add(row.noteID);
    }
  }
  for (const g of taskGroups.values()) {
    g.notes.sort((a, b) => new Date(a.activityDate) - new Date(b.activityDate));
  }

  const inserted = new Set();
  const result = [];
  for (const row of items) {
    if (row.activityType === 'Task' && row.taskID && taskGroups.has(row.taskID)) {
      if (!inserted.has(row.taskID)) {
        result.push(taskGroups.get(row.taskID));
        inserted.add(row.taskID);
      }
      continue;
    }
    if (row.activityType === 'Note' && consumedNoteIds.has(row.noteID)) continue;
    result.push(row);
  }
  return result;
};

const SubTypeChip = ({ subType }) => {
  if (!subType) return null;
  const c = SUBTYPE_TOKEN[subType] || B.textMd;
  return (
    <span style={{
      display: "inline-block", padding: "1px 8px", borderRadius: radius.pill,
      background: c + "16", color: c, border: `1px solid ${c}2e`, fontSize: font.xs, fontWeight: weight.semibold,
      letterSpacing: 0.4, textTransform: "uppercase", marginTop: 4,
    }}>
      {subType}
    </span>
  );
};

// Task workflow statuses → brand token (kept local; these aren't in the shared
// statusColor deal-status map).
const TASK_STATUS_TOKEN = { "Not Started": B.textMd, "In Progress": B.green, "On Hold": B.grey, "In Review": B.amber, "Complete": B.tealDk };

// Display first name only. If the value is an email (no display name resolved),
// use the local part before the "@" so we never show a full email address.
const firstName = (s) => {
  if (!s) return "";
  const trimmed = String(s).trim();
  if (!trimmed) return "";
  const local = trimmed.includes("@") ? trimmed.split("@")[0] : trimmed;
  return local.split(/[\s.]+/)[0];
};

const toLocalDateInput = (d) => {
  const yr  = d.getFullYear();
  const mo  = String(d.getMonth() + 1).padStart(2, "0");
  const dy  = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${dy}`;
};

const defaultStart = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return toLocalDateInput(d);
};

const defaultEnd = () => toLocalDateInput(new Date());


export const ActivityReport = ({ onDealClick, onTaskClick }) => {
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate,   setEndDate]   = useState(defaultEnd);
  const [rows,      setRows]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [summary,        setSummary]        = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError,   setSummaryError]   = useState(null);
  // Bumped on each Summarize/Run so a poll loop from a stale range/request is
  // ignored once a newer one starts.
  const summaryRunId = useRef(0);

  const runReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    // A new range invalidates any in-flight summary poll for the previous range.
    summaryRunId.current += 1;
    setSummary(null);
    setSummaryError(null);
    setSummaryLoading(false);
    try {
      const data = await api.get(`activity-report?startDate=${startDate}&endDate=${endDate}`);
      setRows(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const runSummary = useCallback(async ({ refresh = false } = {}) => {
    // Generation is async on the backend: the first call enqueues a queue job
    // (dodging the ~45s gateway ceiling that made a busy week fail) and returns
    // `pending`; we poll until it's `ready` (or terminal). A run id guards
    // against a poll loop outliving a range change.
    const runId = ++summaryRunId.current;
    setSummaryLoading(true);
    setSummaryError(null);

    const sleep = (ms) => new Promise(res => setTimeout(res, ms));
    const POLL_MS = 3000;
    const MAX_POLLS = 80; // ~4 min ceiling — worker has a 3 min Claude budget

    try {
      const q = `activity-report/summary?startDate=${startDate}&endDate=${endDate}${refresh ? '&refresh=1' : ''}`;
      let data = await api.get(q);
      let tries = 0;
      // Re-fetch without refresh once polling starts (we only want to trigger a
      // regeneration once, then watch for it to land). `pending` is the only
      // non-terminal state — every other status is final for this range.
      const pollQ = `activity-report/summary?startDate=${startDate}&endDate=${endDate}`;
      while (data.status === 'pending' && tries < MAX_POLLS) {
        if (summaryRunId.current !== runId) return; // a newer run superseded us
        await sleep(POLL_MS);
        tries += 1;
        data = await api.get(pollQ);
      }
      if (summaryRunId.current !== runId) return;
      setSummary(data);
    } catch (e) {
      if (summaryRunId.current !== runId) return;
      setSummaryError(e.message);
    } finally {
      if (summaryRunId.current === runId) setSummaryLoading(false);
    }
  }, [startDate, endDate]);

  // Auto-load on first mount with the default last-7-days range.
  useEffect(() => { runReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Combine task events first (across the whole response, so a note and its
  // task always link even if their deal grouping keys would have differed),
  // then group the resulting rows by deal.
  const combined = combineTaskEvents(rows || []);
  const groups = [];
  const seen = {};
  for (const row of combined) {
    const key = row.deal || "—";
    if (!seen[key]) {
      seen[key] = { deal: key, dealId: row.dealID || null, items: [] };
      groups.push(seen[key]);
    }
    seen[key].items.push(row);
  }

  const totalCount = (rows || []).length;
  const noteCount  = (rows || []).filter(r => r.activityType === "Note").length;
  const taskCount  = (rows || []).filter(r => r.activityType === "Task").length;

  // Render the report as a structured PDF (proper text, not a screenshot).
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
    const navy = [35, 76, 90];        // B.navy
    const cream = [248, 243, 237];    // B.creamLt
    const textMd = [74, 101, 114];    // B.textMd

    doc.setFont('helvetica', 'bold').setFontSize(16);
    doc.text('Activity Report', 40, 40);

    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...textMd);
    const range = `${fmt.date(startDate)} – ${fmt.date(endDate)}`;
    const meta = `${totalCount} item${totalCount !== 1 ? 's' : ''} · ${noteCount} note${noteCount !== 1 ? 's' : ''} · ${taskCount} task event${taskCount !== 1 ? 's' : ''}`;
    doc.text(`${range}    ${meta}`, 40, 58);
    doc.setTextColor(0, 0, 0);

    let y = 78;
    for (const group of groups) {
      if (y > doc.internal.pageSize.getHeight() - 80) { doc.addPage(); y = 40; }
      doc.setFillColor(...cream);
      doc.rect(40, y - 12, doc.internal.pageSize.getWidth() - 80, 18, 'F');
      doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...navy);
      doc.text(group.deal, 46, y);
      doc.setTextColor(0, 0, 0);
      y += 10;

      const body = group.items.map(row => {
        const date = fmt.date(row.activityDate);
        let type;
        if (row._kind === 'task') {
          type = ['Task', ...row.subTypes.map(s => s.toUpperCase())].join('\n');
        } else if (row.activitySubType) {
          type = `${row.activityType}\n${row.activitySubType.toUpperCase()}`;
        } else {
          type = row.activityType;
        }
        let description;
        if (row._kind === 'task') {
          const lines = [row.taskTitle || ''];
          for (const n of row.notes) {
            const txt = noteToText(n.description);
            if (txt) lines.push(`• ${txt}`);
          }
          description = lines.join('\n');
        } else if (row.activityType === 'Note') {
          const txt = noteToText(row.description);
          description = row.taskTitle ? `${row.taskTitle} — ${txt}` : txt;
        } else {
          description = row.description || '';
        }
        const assigned = row.assignedTo
          ? row.assignedTo.split(',').map(s => firstName(s)).filter(Boolean).join(', ')
          : '';
        const author = row._kind === 'task'
          ? [...new Set((row.authors || []).map(firstName).filter(Boolean))].join(', ')
          : firstName(row.author || '');
        return [date, type, description, assigned, author];
      });

      autoTable(doc, {
        startY: y,
        head: [['Date', 'Type', 'Description', 'Assigned', 'Author']],
        body,
        styles: { fontSize: 9, cellPadding: 4, valign: 'top', overflow: 'linebreak' },
        headStyles: { fillColor: navy, textColor: 255, fontStyle: 'bold', fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 75 },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 90 },
          4: { cellWidth: 80 },
        },
        margin: { left: 40, right: 40 },
        theme: 'grid',
      });
      y = doc.lastAutoTable.finalY + 18;
    }

    doc.save(`activity-report-${startDate}-to-${endDate}.pdf`);
  };

  // Render the Claude narrative summary (markdown) as a clean, readable PDF —
  // headings per deal, bullets, and paragraphs — as opposed to exportPDF above,
  // which lays out the raw event rows in a table. Enabled only once a summary
  // has been generated.
  const exportSummaryPDF = () => {
    if (!summary?.markdown) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const navy = [35, 76, 90];     // B.navy
    const textMd = [74, 101, 114]; // B.textMd
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const left = 48, right = 48;
    const contentW = pageW - left - right;
    let y = 56;

    const ensureSpace = (h) => { if (y + h > pageH - 48) { doc.addPage(); y = 56; } };
    const stripInline = (s) => s.replace(/\*\*/g, '').replace(/`/g, '');

    doc.setFont('helvetica', 'bold').setFontSize(18).setTextColor(...navy);
    doc.text('Activity Summary', left, y);
    y += 18;
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...textMd);
    doc.text(`${fmt.date(startDate)} – ${fmt.date(endDate)}`, left, y);
    y += 8;
    doc.setDrawColor(...navy).setLineWidth(1.2).line(left, y, pageW - right, y);
    y += 16;
    doc.setTextColor(0, 0, 0);

    const lines = summary.markdown.replace(/\r\n/g, '\n').split('\n');
    for (const raw of lines) {
      const line = raw.trimEnd();
      if (!line.trim()) { y += 6; continue; }

      const h = line.match(/^(#{1,3})\s+(.*)$/);
      if (h) {
        const size = h[1].length === 1 ? 14 : h[1].length === 2 ? 12.5 : 11;
        y += h[1].length <= 2 ? 8 : 4;
        doc.setFont('helvetica', 'bold').setFontSize(size).setTextColor(...navy);
        const wrapped = doc.splitTextToSize(stripInline(h[2]), contentW);
        for (const w of wrapped) { ensureSpace(size + 4); doc.text(w, left, y); y += size + 3; }
        doc.setTextColor(0, 0, 0);
        y += 3;
        continue;
      }

      const bullet = line.match(/^\s*([-*]|\d+\.)\s+(.*)$/);
      if (bullet) {
        const marker = /^\d+\./.test(bullet[1].trim()) ? `${bullet[1].trim()} ` : '•  ';
        doc.setFont('helvetica', 'normal').setFontSize(10.5).setTextColor(0, 0, 0);
        const indent = 16;
        const wrapped = doc.splitTextToSize(stripInline(bullet[2]), contentW - indent);
        for (let i = 0; i < wrapped.length; i++) {
          ensureSpace(15);
          if (i === 0) doc.text(marker, left, y);
          doc.text(wrapped[i], left + indent, y);
          y += 14;
        }
        y += 2;
        continue;
      }

      doc.setFont('helvetica', 'normal').setFontSize(10.5).setTextColor(0, 0, 0);
      const wrapped = doc.splitTextToSize(stripInline(line), contentW);
      for (const w of wrapped) { ensureSpace(15); doc.text(w, left, y); y += 14; }
      y += 3;
    }

    doc.save(`activity-summary-${startDate}-to-${endDate}.pdf`);
  };

  const headerCells = [
    ["0 0 100px", "Date"],
    ["0 0 70px",  "Type"],
    ["1 1 280px", "Description"],
    ["0 0 140px", "Assigned"],
    ["0 0 130px", "Author"],
  ];

  return (
    <div>
      <PageHeader
        icon="activityReport"
        title="Activity Report"
        subtitle={rows !== null ? `${totalCount} item${totalCount !== 1 ? "s" : ""} — ${noteCount} note${noteCount !== 1 ? "s" : ""}, ${taskCount} task event${taskCount !== 1 ? "s" : ""}` : undefined}
        actions={rows !== null && rows.length > 0 ? (
          <div style={{ display: "flex", gap: space.sm }}>
            <Btn size="sm" variant="primary" onClick={() => runSummary()} loading={summaryLoading}>
              <Icon name="sparkles" size={14} /> Summarize
            </Btn>
            <Btn size="sm" variant="secondary" onClick={exportPDF}><Icon name="download" size={14} /> PDF</Btn>
          </div>
        ) : null}
      />

      {/* Date range filter */}
      <Card style={{ marginBottom: space.xl, padding: "14px 20px" }}>
        <div style={{ display: "flex", gap: space.md, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <label style={{ display: "block", fontSize: font.xs, fontWeight: weight.semibold, color: B.textMd, marginBottom: space.xs, letterSpacing: 0.5, textTransform: "uppercase" }}>Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              style={{ padding: "8px 11px", border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.base, fontFamily: fonts.sans, background: B.surface, outline: "none" }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: font.xs, fontWeight: weight.semibold, color: B.textMd, marginBottom: space.xs, letterSpacing: 0.5, textTransform: "uppercase" }}>End date</label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              style={{ padding: "8px 11px", border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.base, fontFamily: fonts.sans, background: B.surface, outline: "none" }}
            />
          </div>
          <Btn variant="primary" onClick={runReport} loading={loading}>
            Run Report
          </Btn>
        </div>
      </Card>

      {/* Claude summary of the period — populated on demand via "Summarize". */}
      {(summaryLoading || summaryError || summary) && (
        <Card style={{ marginBottom: space.xl, padding: "16px 20px", borderLeft: `3px solid ${B.navy}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: space.sm, marginBottom: space.sm }}>
            <Icon name="sparkles" size={15} color={B.navy} />
            <span style={{ fontSize: font.xs, fontWeight: weight.semibold, color: B.textMd, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Summary
            </span>
            <span style={{ fontSize: font.xs, color: B.textLt, marginLeft: "auto" }}>
              {fmt.date(startDate)} – {fmt.date(endDate)}
            </span>
            {summary?.markdown && (
              <Btn size="sm" variant="secondary" onClick={exportSummaryPDF}>
                <Icon name="download" size={13} /> PDF
              </Btn>
            )}
          </div>
          {summaryLoading ? (
            <div style={{ fontSize: font.sm, color: B.textLt, fontStyle: "italic" }}>
              Summarizing activity… this can take a minute on a busy period.
            </div>
          ) : summaryError ? (
            <ErrorMsg msg={summaryError} onRetry={() => runSummary({ refresh: true })} />
          ) : summary?.html ? (
            <div className="activity-summary" dangerouslySetInnerHTML={{ __html: summary.html }} />
          ) : summary?.status === 'empty' ? (
            <div style={{ fontSize: font.sm, color: B.textLt, fontStyle: "italic" }}>No activity to summarize for this range.</div>
          ) : summary?.status === 'pending' ? (
            <div style={{ fontSize: font.sm, color: B.textLt }}>
              <span style={{ fontStyle: "italic" }}>
                The summary is still being generated in the background. Check back in a moment — the full activity is listed below.
              </span>
              <div style={{ marginTop: space.sm }}>
                <Btn size="sm" variant="secondary" onClick={() => runSummary()}>Check again</Btn>
              </div>
            </div>
          ) : summary?.status === 'error' ? (
            <div style={{ fontSize: font.sm, color: B.textLt }}>
              <span style={{ fontStyle: "italic" }}>
                {summary?.reason === 'timeout'
                  ? "The summary took too long for this range. Try a shorter date range — the full activity is listed below."
                  : "The summary couldn't be generated for this range. Try again shortly, or use a shorter date range. The full activity is listed below."}
              </span>
              {summary?.message && (
                <div style={{ marginTop: space.xs, fontSize: font.xs, color: B.textLt }}>Details: {summary.message}</div>
              )}
              <div style={{ marginTop: space.sm }}>
                <Btn size="sm" variant="secondary" onClick={() => runSummary({ refresh: true })}>Retry summary</Btn>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: font.sm, color: B.textLt, fontStyle: "italic" }}>
              A summary couldn&apos;t be generated (AI summarization isn&apos;t configured). The full activity is listed below.
            </div>
          )}
        </Card>
      )}

      {/* Results */}
      {loading && <Spinner />}
      {error && <ErrorMsg msg={error} onRetry={runReport} />}

      {!loading && !error && rows === null && (
        <EmptyState icon="activityReport" title="Ready to run" message="Select a date range and click Run Report." />
      )}

      {!loading && !error && rows !== null && rows.length === 0 && (
        <EmptyState icon="activityReport" title="No activity" message="No activity found for this date range." />
      )}

      {!loading && !error && groups.length > 0 && (
        <>
          {/* Column header */}
          <div style={{
            display: "flex", background: B.surfaceMuted, border: `1px solid ${B.border}`,
            borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg,
            padding: "11px 16px", gap: 8, position: "sticky", top: 58, zIndex: 10,
          }}>
            {headerCells.map(([flex, label]) => (
              <div key={label} style={{ flex, fontSize: font.xs, fontWeight: weight.semibold, color: B.textMd, letterSpacing: 0.5, textTransform: "uppercase" }}>
                {label}
              </div>
            ))}
          </div>

          {/* Rows container */}
          <div style={{ border: `1px solid ${B.border}`, borderTop: "none", borderBottomLeftRadius: radius.lg, borderBottomRightRadius: radius.lg, background: B.surface, overflow: "hidden" }}>
          {/* Deal groups */}
          {groups.map((group, gi) => (
            <div key={group.deal}>
              {/* Deal / DealDetail sub-heading — styled to match the Tasks list group header */}
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                background: B.surfaceMuted,
                padding: "10px 16px",
                borderTop: gi > 0 ? `1px solid ${B.border}` : "none",
                borderBottom: `1px solid ${B.border}`,
                borderLeft: `3px solid ${B.navy}`,
              }}>
                <div
                  style={{
                    flex: 1, fontWeight: weight.bold, fontSize: font.base, color: B.text,
                    cursor: group.dealId && onDealClick ? "pointer" : "default",
                    textDecoration: group.dealId && onDealClick ? "underline" : "none",
                    textUnderlineOffset: 3,
                  }}
                  onClick={() => group.dealId && onDealClick && onDealClick(group.dealId)}
                >
                  {group.deal}
                </div>
                <span style={{
                  fontSize: font.xs, color: B.textMd, background: B.surface,
                  borderRadius: radius.pill, padding: "1px 9px", border: `1px solid ${B.border}`, fontWeight: weight.semibold,
                }}>
                  {group.items.length}
                </span>
              </div>

              {/* Rows */}
              {group.items.map((row, ri) => {
                const rowBg = ri % 2 === 0 ? B.surface : B.surfaceMuted;
                const isTask = row.activityType === "Task";
                const isCombinedTask = row._kind === 'task';
                const isHighPriority = isTask && (row.priority === "High" || row.priority === "Critical");
                const today = new Date(); today.setHours(0, 0, 0, 0);
                const due = isTask && row.dueDate ? new Date(row.dueDate) : null;
                if (due) due.setHours(0, 0, 0, 0);
                const incomplete = isTask && row.status !== "Complete";
                const overdue   = !!(due && incomplete && due < today);
                const dueSoon   = !!(due && incomplete && !overdue && (due - today) <= 7 * 86400000);
                const showDue   = !!(due && (overdue || dueSoon || isHighPriority));
                const showMeta  = isTask && (isHighPriority || showDue);
                const dueColor  = overdue ? B.red : dueSoon ? B.navyLt : B.textMd;
                const rowKey = isCombinedTask
                  ? `task-${row.taskID}`
                  : `${row.activityType}-${row.activitySubType || ""}-${row.noteID || row.taskID || ri}`;
                const authorText = isCombinedTask
                  ? [...new Set(row.authors.map(firstName).filter(Boolean))].join(', ')
                  : firstName(row.author);
                return (
                  <div
                    key={rowKey}
                    style={{
                      display: "flex", gap: 8, padding: "10px 16px",
                      background: rowBg,
                      borderBottom: `1px solid ${B.divider}`,
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = B.hover}
                    onMouseLeave={e => e.currentTarget.style.background = rowBg}
                  >
                    <div style={{ flex: "0 0 100px", fontSize: font.sm, color: B.textMd }}>
                      {fmt.date(row.activityDate)}
                    </div>
                    <div style={{ flex: "0 0 70px", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <TypeBadge type={row.activityType} />
                      {isCombinedTask
                        ? row.subTypes.map(st => <SubTypeChip key={st} subType={st} />)
                        : <SubTypeChip subType={row.activitySubType} />}
                    </div>
                    <div style={{ flex: "1 1 280px", fontSize: font.base, color: B.text, lineHeight: 1.5 }}>
                      {isCombinedTask ? (() => {
                        const link = row.taskID && onTaskClick ? () => onTaskClick(row.taskID) : null;
                        const titleStyle = link
                          ? { color: B.navy, cursor: "pointer", fontWeight: weight.semibold }
                          : { fontWeight: weight.semibold };
                        const title = (
                          <span
                            onClick={link || undefined}
                            style={titleStyle}
                            onMouseEnter={link ? e => e.currentTarget.style.textDecoration = "underline" : undefined}
                            onMouseLeave={link ? e => e.currentTarget.style.textDecoration = "none" : undefined}
                          >
                            {row.taskTitle}
                          </span>
                        );
                        return (
                          <>
                            {title}
                            {row.notes.length > 0 && (
                              <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
                                {row.notes.map((n, i) => (
                                  <li key={n.noteID || i} style={{ fontSize: font.sm, color: B.textMd, marginTop: 2 }}>
                                    {noteToText(n.description)}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </>
                        );
                      })() : (() => {
                        const body = row.activityType === 'Note' ? noteToText(row.description) : row.description;
                        const link = row.taskID && onTaskClick
                          ? () => onTaskClick(row.taskID)
                          : (row.dealID && onDealClick ? () => onDealClick(row.dealID) : null);
                        if (!body && !row.taskTitle) return <span style={{ color: B.textLt, fontStyle: "italic" }}>—</span>;
                        const inner = row.activityType === 'Note' && row.taskTitle
                          ? <><span style={{ fontWeight: weight.semibold }}>{row.taskTitle}</span>{body ? ` — ${body}` : ''}</>
                          : body;
                        if (!link) return inner;
                        return (
                          <span
                            onClick={link}
                            style={{ color: B.navy, cursor: "pointer" }}
                            onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                            onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
                          >
                            {inner}
                          </span>
                        );
                      })()}
                      {showMeta && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6, fontSize: font.xs, color: B.textMd, alignItems: "center" }}>
                          {isHighPriority && (
                            <span style={{
                              padding: "1px 8px", borderRadius: radius.pill,
                              border: `1px solid ${statusColor(row.priority)}`,
                              color: statusColor(row.priority),
                              fontWeight: weight.bold, fontSize: font.xs, letterSpacing: 0.3,
                            }}>{row.priority}</span>
                          )}
                          {isHighPriority && row.status && (
                            <span style={{
                              padding: "1px 8px", borderRadius: radius.pill,
                              background: (TASK_STATUS_TOKEN[row.status] || B.textLt) + "1c",
                              border: `1px solid ${(TASK_STATUS_TOKEN[row.status] || B.textLt)}2e`,
                              color: TASK_STATUS_TOKEN[row.status] || B.textMd, fontWeight: weight.bold, fontSize: font.xs, letterSpacing: 0.3,
                            }}>{row.status}</span>
                          )}
                          {showDue && (
                            <span style={{ color: dueColor, fontWeight: (overdue || dueSoon) ? weight.bold : weight.medium }}>
                              Due {fmt.date(row.dueDate)}{overdue ? " · overdue" : dueSoon ? " · this week" : ""}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ flex: "0 0 140px", fontSize: font.sm, color: B.textMd }}>
                      {row.assignedTo
                        ? row.assignedTo.split(',').map(s => firstName(s)).filter(Boolean).join(', ')
                        : <span style={{ color: B.textLt, fontStyle: "italic" }}>—</span>}
                    </div>
                    <div style={{ flex: "0 0 130px", fontSize: font.sm, color: B.textMd }}>
                      {authorText || <span style={{ color: B.textLt, fontStyle: "italic" }}>—</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          </div>
        </>
      )}

      <style>{`
        .activity-summary { font-size: 13px; color: ${B.text}; line-height: 1.55; }
        .activity-summary h1 { font-size: 16px; font-weight: 700; color: ${B.navy}; margin: 14px 0 4px 0; line-height: 1.3; }
        .activity-summary h2 { font-size: 15px; font-weight: 700; color: ${B.navy}; margin: 16px 0 4px 0; line-height: 1.3; border-bottom: 1px solid ${B.divider}; padding-bottom: 4px; }
        .activity-summary h2:first-child, .activity-summary h1:first-child { margin-top: 0; }
        .activity-summary h3 { font-size: 13px; font-weight: 700; color: ${B.textMd}; margin: 10px 0 2px 0; line-height: 1.3; text-transform: uppercase; letter-spacing: 0.5px; }
        .activity-summary p { margin: 6px 0; }
        .activity-summary ul, .activity-summary ol { margin: 4px 0; padding-left: 22px; }
        .activity-summary li { margin: 2px 0; }
        .activity-summary a { color: #2563eb; text-decoration: underline; }
        .activity-summary strong { font-weight: 700; }
        @media print {
          body { font-size: 11px; }
          nav, button { display: none !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
};
